<template>
  <div
    class="block-auth-free-participation"
    :class="{
      'is-on-grid': isOnGrid,
    }"
  >
    <div class="block-auth-free-participation__inner">
      <AtomTextHeading
        v-if="displayTitle"
        :text="displayTitle"
        font-size="h2"
        class="block-auth-free-participation__title"
      />

      <AtomTextRichtext
        v-if="displayText"
        :html="displayText"
        class="block-auth-free-participation__text"
      />

      <div
        v-if="showLoading"
        class="block-auth-free-participation__loading"
        aria-busy="true"
      />

      <AtomTextRichtext
        v-else-if="showRedeemed"
        :html="redeemedHtml"
        class="block-auth-free-participation__redeemed"
      />

      <div
        v-else-if="showForm"
        class="block-auth-free-participation__form"
      >
        <Transition name="fade-in">
          <UtilRendererForm
            v-if="fields?.length > 0"
            :fields="fields"
            :submit-text="submitText"
            :button-alignment="buttonAlignment"
            :success-message="successMessage"
            :error-message="errorMessage"
            :callback="submitCallback"
            :use-reset="false"
            :has-link-action="false"
          />
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    isOnGrid: {
        type: Boolean,
        default: false,
    },

    buttonAlignment: {
        type: String,
        default: 'left',
        validator: (value) => ['left', 'center', 'right'].includes(value),
    },

    fields: {
        type: Array,
        default: () => [],
    },

    successMessage: {
        type: String,
        default: '',
    },

    errorMessage: {
        type: String,
        default: '',
    },

    submitText: {
        type: String,
        default: '',
    },

    title: {
        type: String,
        default: '',
    },

    text: {
        type: String,
        default: '',
    },

    hideTitle: {
        type: Boolean,
        default: false,
    },

    hideText: {
        type: Boolean,
        default: false,
    },

    redeemedHtml: {
        type: String,
        default: '',
    },
});

const displayTitle = computed(() => (props.hideTitle ? '' : props.title));
const displayText = computed(() => (props.hideText ? '' : props.text));

const { pending } = useLazyAsyncData(
    useUserMe,
    { server: false },
);

const redemptionStateKnown = computed(
    () => typeof user.value?.free_participation_redeemed === 'boolean',
);

const showLoading = computed(() => pending.value || !redemptionStateKnown.value);
const showRedeemed = computed(
    () => !pending.value
        && redemptionStateKnown.value
        && user.value.free_participation_redeemed === true,
);
const showForm = computed(
    () => !pending.value
        && redemptionStateKnown.value
        && user.value.free_participation_redeemed === false,
);

const submitCallback = async (formData) => {
    const merged = {
        ...formData,
        ...(user.value?.email ? { email: user.value.email } : {}),
    };
    await useFreeParticipation(merged);
    await useUserMe();
};
</script>

<style lang="scss" scoped>
.block-auth-free-participation {
    &.is-on-grid {
        @include wrapper-layout;
        @include grid-layout();

        margin-bottom: var(--m-bottom--big);
    }
}

.block-auth-free-participation__inner {
    @include box-default;
    @include fluid('row-gap', 20px, 15px, 20px);
    @include default-content-columns('wide');

    position: relative;
    display: flex;
    width: 100%;
    flex-direction: column;
    row-gap: 20px;

    ::v-deep(p) {
        font-size: var(--f-size--h5);
    }
}

.block-auth-free-participation__text,
.block-auth-free-participation__title,
.block-auth-free-participation__redeemed {
    width: 100%;
    max-width: 641px;
}

.block-auth-free-participation__loading {
    min-height: 120px;
}

.block-auth-free-participation__form {
    width: 100%;
}
</style>
